import React, { useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Paper,
  Button,
  IconButton,
  TextField,
  Box,
  Chip,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  CircularProgress
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import { formatDate } from '../../../utils/DateUtils';
import TableRefreshButton from '../../../components/TableRefreshButton/TableRefreshButton';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import { getProformaStatusColor, getProformaStatusText } from '../../../utils/ProformaStatusUtils';
import { formatMoney } from '../../../utils/currency';

const ProformaActionsMenu = ({
  row,
  readOnlyActions,
  onView,
  onConvertToOrder,
  onEdit,
  onDownloadPDF,
  onViewInvoice,
  loadingInvoiceId,
  onReject,
  onSendEmail,
  t
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const isSent = row.status === 'SENT';

  const handleOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const runAction = (action) => (event) => {
    event.stopPropagation();
    handleClose();
    action(row);
  };

  // Una proforma aceptada ya no se puede operar: solo se consulta.
  if (row.status === 'ACCEPTED') {
    // En la vista de un local, el pedido entregado ya tiene su factura: se abre desde acá.
    const canViewInvoice = readOnlyActions && row.generatedOrderId && row.generatedOrderStatus === 'FINALIZED';
    const isLoadingInvoice = loadingInvoiceId === row.id;
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
        <IconButton size="small" onClick={runAction(onView)} title={t('view')}>
          {getIcon('eye')}
        </IconButton>
        {canViewInvoice && (
          <IconButton
            size="small"
            onClick={runAction(onViewInvoice)}
            disabled={isLoadingInvoice}
            title={t('invoice')}
          >
            {isLoadingInvoice ? <CircularProgress size={18} /> : getIcon('reader')}
          </IconButton>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
      {!readOnlyActions && isSent && (
        <>
          <IconButton
            size="small"
            sx={{ color: 'success.main' }}
            onClick={runAction(onConvertToOrder)}
            title={t('convert_to_order')}
          >
            {getIcon('checkmarkDone')}
          </IconButton>
          <IconButton
            size="small"
            sx={{ color: 'primary.main' }}
            onClick={runAction(onEdit)}
            title={t('edit') === 'edit' ? 'Editar' : t('edit')}
          >
            {getIcon('settings')}
          </IconButton>
        </>
      )}
      <IconButton
        size="small"
        onClick={handleOpen}
        title={t('more_options') || 'Más opciones'}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={runAction(onView)}>
          <ListItemIcon>{getIcon('eye')}</ListItemIcon>
          <ListItemText>{t('view')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={runAction(onDownloadPDF)}>
          <ListItemIcon>{getIcon('download')}</ListItemIcon>
          <ListItemText>{t('view_pdf')}</ListItemText>
        </MenuItem>
        {!readOnlyActions && (
          <MenuItem onClick={runAction(onSendEmail)}>
            <ListItemIcon sx={{ color: 'primary.main' }}>{getIcon('mail')}</ListItemIcon>
            <ListItemText>{t('send_proforma_email')}</ListItemText>
          </MenuItem>
        )}
        {!readOnlyActions && isSent && (
          <MenuItem onClick={runAction(onReject)}>
            <ListItemIcon sx={{ color: 'error.main' }}>{getIcon('close')}</ListItemIcon>
            <ListItemText>{t('reject_order')}</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

const B2BProformaTable = ({ 
  proformas = [], 
  totalRows = 0,
  paginationModel = { page: 0, pageSize: 10 },
  onPaginationChange,
  sortModel = [],
  onSortModelChange,
  filterModel = { items: [] },
  statusFilter = '',
  onStatusFilterChange,
  onFilterModelChange,
  searchTerm = '',
  onSearchChange,
  onRefresh,
  onAdd, 
  onView, 
  onConvertToOrder, 
  onEdit,
  onDownloadPDF,
  onViewInvoice,
  loadingInvoiceId,
  onReject,
  onSendEmail,
  showAddButton = true,
  addButtonLabel,
  readOnlyActions = false,
  loading 
}) => {
  const { t } = useTranslation();
  
  dayjs.extend(relativeTime);
  dayjs.locale('es');

  const columns = [
    { 
      field: 'id', 
      headerName: t('proforma_number'), 
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      )
    },
    { 
      field: 'clientName', 
      headerName: t('client'), 
      flex: 1.5,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const clientName = params.row.nombreFantasia || params.row.razonSocial || params.value || t('unassigned');
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
            <Typography variant="body2">
              {clientName}
            </Typography>
          </Box>
        );
      }
    },
    { 
      field: 'branchName', 
      headerName: t('branch'), 
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
          <Typography variant="body2">
            {params.value || `Sucursal ${params.row.branchId}`}
          </Typography>
        </Box>
      )
    },
    {
      field: 'issueDate',
      headerName: t('date'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const date = dayjs(params.value);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
            <Typography variant="body2">{formatDate(date)}</Typography>
          </Box>
        );
      }
    },
    { 
      field: 'totalAmount', 
      headerName: t('total_amount'), 
      type: 'number', 
      flex: 1, 
      align: 'center', 
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
          <Typography variant="body2">
            {typeof params.value === 'number' ? formatMoney(params.value) : '—'}
          </Typography>
        </Box>
      )
    },
    {
      field: 'status',
      headerName: t('status'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const effectiveStatus = readOnlyActions && params.row.generatedOrderStatus
          ? params.row.generatedOrderStatus
          : params.value;

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
            <Chip
              label={getProformaStatusText(effectiveStatus, t)}
              color={getProformaStatusColor(effectiveStatus)}
              size="small"
            />
          </Box>
        );
      }
    },
    {
      field: 'actions',
      headerName: t('actions'),
      flex: 0.9,
      minWidth: 140,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <ProformaActionsMenu
          row={params.row}
          readOnlyActions={readOnlyActions}
          onView={onView}
          onConvertToOrder={onConvertToOrder}
          onEdit={onEdit}
          onDownloadPDF={onDownloadPDF}
          onViewInvoice={onViewInvoice}
          loadingInvoiceId={loadingInvoiceId}
          onReject={onReject}
          onSendEmail={onSendEmail}
          t={t}
        />
      )
    }
  ];

  return (
    <Paper sx={{ padding: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label={t('search')}
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={onSearchChange}
          placeholder={t('search_by_proforma_client')}
          sx={{ width: 300 }}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          {onStatusFilterChange && (
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="proforma-status-filter-label" shrink>{t('status')}</InputLabel>
              <Select
                labelId="proforma-status-filter-label"
                label={t('status')}
                displayEmpty
                notched
                value={statusFilter}
                onChange={(event) => onStatusFilterChange(event.target.value)}
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="SENT">Pendientes</MenuItem>
                <MenuItem value="ACCEPTED">Aceptadas</MenuItem>
                <MenuItem value="REJECTED">Rechazadas</MenuItem>
              </Select>
            </FormControl>
          )}
          <TableRefreshButton loading={loading} onClick={onRefresh} />
          {showAddButton && (
            <Button variant="outlined" startIcon={getIcon('add')} onClick={onAdd}>
              {addButtonLabel || t('add_proforma')}
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: readOnlyActions ? 860 : 1000 }}>
          <DataGrid
            loading={loading}
            rows={proformas}
            columns={columns}
            paginationMode="server"
            rowCount={totalRows}
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationChange}
            sortModel={sortModel}
            onSortModelChange={onSortModelChange}
            sortingMode="server"
            filterModel={filterModel}
            onFilterModelChange={onFilterModelChange}
            filterMode="server"
            pageSizeOptions={[10, 25, 50]}
            getRowId={(row) => row.id }
            autoHeight
            disableRowSelectionOnClick
            hideFooterSelectedRowCount
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default B2BProformaTable;
